/* ============================================================
   CART + PRODUCT ART (cart.js)
   ------------------------------------------------------------
   A real client-side cart: line items, quantity steppers,
   subtotal, persistent across visits (localStorage). The
   checkout button is the Shopify seam.

   SHOPIFY MIGRATION
   -----------------
   This module maps 1:1 onto Shopify's AJAX cart:
     add()        → POST /cart/add.js   {id: variantId, quantity}
     setQty()     → POST /cart/change.js
     lines/count  → GET  /cart.js
     checkout()   → window.location = '/checkout'
   Keep the drawer UI; swap the storage layer for those calls
   (or Storefront API cartCreate/cartLinesAdd + checkoutUrl on a
   headless build). Shipping & taxes stay "calculated at
   checkout" — that's standard; Shopify computes them there.

   PRODUCT ART
   -----------
   Until real product photos exist, each product renders a custom
   line-art mark (art key on the product in data.js). The moment
   a product gets `image: 'https://…'` (Shopify featured image),
   the art is replaced automatically — no other changes.
   ============================================================ */

/** Shopify CDN image resizing — CRITICAL for mobile stability.
    Raw store uploads can be 10+ MB PNGs; six of those plus blur
    backdrops crashes mobile browser tabs ("Can't open this page").
    Appending ?width=N makes Shopify's CDN serve a right-sized file. */
function shopifyImg(url, width) {
  if (!url || !url.includes('cdn.shopify.com')) return url;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
}

/** Line-art SVG per product type. stroke = currentColor so the
    product's accent color tints it. */
function productArtSVG(product) {
  if (product.image) {
    return `<img class="product-photo" loading="lazy"
      src="${shopifyImg(product.image, 300)}" alt="${product.name}"/>`;
  }
  const P = {
    hoodie: `
      <path d="M35 34 Q50 16 65 34 Q58 40 50 40 Q42 40 35 34 Z"/>
      <path d="M35 34 Q50 44 65 34 L74 39 L80 62 L70 66 L70 86 Q50 91 30 86 L30 66 L20 62 L26 39 Z"/>
      <path d="M46 42 L46 52 M54 42 L54 52"/>
      <path d="M40 70 L60 70 L57 80 L43 80 Z"/>`,
    tee: `
      <path d="M36 28 Q50 36 64 28 L78 40 L70 50 L68 46 L68 84 L32 84 L32 46 L30 50 L22 40 Z"/>
      <path d="M43 28 Q50 34 57 28"/>`,
    vinyl: `
      <circle cx="50" cy="50" r="34"/>
      <circle cx="50" cy="50" r="13"/>
      <circle cx="50" cy="50" r="2.5"/>
      <path d="M50 22 A28 28 0 0 1 78 50 M50 28 A22 22 0 0 1 72 50"/>`,
    cap: `
      <path d="M26 54 Q26 30 50 30 Q74 30 74 54 Z"/>
      <path d="M26 54 Q52 62 88 56 Q90 60 86 61 Q52 68 25 59 Z"/>
      <path d="M50 30 L50 24 M50 42 L50 54"/>`,
    jacket: `
      <path d="M40 30 L50 38 L60 30 L72 34 L80 52 L70 57 L70 86 L30 86 L30 57 L20 52 L28 34 Z"/>
      <path d="M50 38 L50 86"/>
      <circle cx="45" cy="50" r="1.6"/><circle cx="45" cy="62" r="1.6"/>
      <circle cx="45" cy="74" r="1.6"/>
      <path d="M30 57 L30 50 M70 57 L70 50"/>`,
  };
  const art = P[product.art] ||
    `<text x="50" y="58" text-anchor="middle" font-size="30"
       font-weight="800" fill="currentColor" stroke="none">OP</text>`;
  return `<svg class="product-art" viewBox="0 0 100 100" fill="none"
    stroke="currentColor" stroke-width="3" stroke-linecap="round"
    stroke-linejoin="round" aria-hidden="true">${art}</svg>`;
}

const Cart = (() => {
  const KEY = 'op_cart';
  let lines = [];   // [{productId, variantId, label, qty}]

  const $ = s => document.querySelector(s);
  const money = n => '$' + n.toFixed(2).replace(/\.00$/, '');

  function loadState() {
    try { lines = JSON.parse(localStorage.getItem(KEY) || '[]'); }
    catch (_) { lines = []; }
  }
  function save() {
    try { localStorage.setItem(KEY, JSON.stringify(lines)); } catch (_) {}
    renderBadge();
  }

  const count = () => lines.reduce((n, l) => n + l.qty, 0);
  const subtotal = () => lines.reduce((s, l) =>
    s + (PRODUCTS[l.productId]?.price || 0) * l.qty, 0);

  /* SHOPIFY SEAM: POST /cart/add.js */
  function add(product, variant) {
    const found = lines.find(l => l.variantId === variant.id);
    if (found) found.qty += 1;
    else lines.push({ productId: product.id, variantId: variant.id,
                      label: variant.label, qty: 1 });
    save();
    open();   // show the cart so the add is never invisible
  }

  /* SHOPIFY SEAM: POST /cart/change.js */
  function setQty(idx, qty) {
    if (!lines[idx]) return;
    lines[idx].qty = qty;
    if (qty <= 0) lines.splice(idx, 1);
    save();
    renderDrawer();
  }

  function checkout() {
    /* REAL CHECKOUT — products.js carries real variant IDs from
       Obed's live store, so a Shopify cart permalink hands this
       bag straight to obedpadilla.com's checkout (shipping, tax,
       payment all real). When the feed itself becomes the store's
       theme, this simplifies to `location = '/checkout'`.        */
    if (!lines.length) return;
    const permalink = `${STORE_URL}/cart/` +
      lines.map(l => `${l.variantId}:${l.qty}`).join(',');
    window.location.assign(permalink);
  }

  function renderBadge() {
    const b = $('#cartCount');
    if (!b) return;
    const n = count();
    b.textContent = n;
    b.style.display = n ? '' : 'none';
  }

  function renderDrawer() {
    const body = $('#cartLines');
    if (!lines.length) {
      body.innerHTML = `<div class="cart-empty">
        <div class="cart-empty__mark">🛍</div>
        <p>Your bag is empty.</p>
        <p class="cart-empty__sub">Everything in the Merch tab ships worldwide.</p>
      </div>`;
      $('#cartFooter').style.display = 'none';
      return;
    }
    body.innerHTML = lines.map((l, i) => {
      const p = PRODUCTS[l.productId];
      return `
        <div class="cart-line" style="--accent:${p.accent}">
          <div class="cart-line__art">${productArtSVG(p)}</div>
          <div class="cart-line__info">
            <div class="cart-line__name">${p.name}</div>
            <div class="cart-line__variant">${l.label}</div>
            <div class="cart-line__qty">
              <button data-cart-dec="${i}" aria-label="Less">−</button>
              <span>${l.qty}</span>
              <button data-cart-inc="${i}" aria-label="More">+</button>
            </div>
          </div>
          <div class="cart-line__right">
            <div class="cart-line__price">${money(p.price * l.qty)}</div>
            <button class="cart-line__remove" data-cart-rm="${i}" aria-label="Remove">✕</button>
          </div>
        </div>`;
    }).join('');
    $('#cartFooter').style.display = '';
    $('#cartSubtotal').textContent = money(subtotal());
    body.querySelectorAll('[data-cart-dec]').forEach(b =>
      b.addEventListener('click', () => setQty(+b.dataset.cartDec, lines[+b.dataset.cartDec].qty - 1)));
    body.querySelectorAll('[data-cart-inc]').forEach(b =>
      b.addEventListener('click', () => setQty(+b.dataset.cartInc, lines[+b.dataset.cartInc].qty + 1)));
    body.querySelectorAll('[data-cart-rm]').forEach(b =>
      b.addEventListener('click', () => setQty(+b.dataset.cartRm, 0)));
  }

  function open() {
    renderDrawer();
    $('#cartSheet').classList.add('sheet--open');
    document.body.classList.add('modal-open');
  }

  function init() {
    loadState();
    renderBadge();
    $('#cartToggle').addEventListener('click', open);
    $('#cartCheckout').addEventListener('click', checkout);
  }

  return { init, add, open, count };
})();
